// src/App.tsx - Clean version with host permissions route added
import React from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, Outlet} from 'react-router-dom';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {Toaster} from 'react-hot-toast';

import {AuthProvider, useAuth} from '@/context/AuthContext';
import {AppLayout} from '@/components/layout/AppLayout';
import {LoadingSpinner} from '@/components/ui';

// Pages
import {Login} from '@/pages/auth/Login';
import Dashboard from '@/pages/Dashboard';
import {ManageOrganizations} from '@/pages/ManageOrganizations';
import {ManageAccounts} from '@/pages/ManageAccounts';
import {AccountDetail} from '@/pages/AccountDetail';
import {ManageGameHost} from '@/pages/ManageGameHost';
import {ManageHostPermissions} from '@/pages/ManageHostPermissions';
import AcceptInvitation from '@/pages/AcceptInvitation';
import GameInstances from '@/pages/GameInstances.tsx';
import ManageAdmins from '@/pages/ManageAdmins';
import {AccountTypeProvider, AccountTypeUrlSync} from "@/context/AccountTypeContext";
import HostAuthWrapper from "@/components/auth/HostAuthWrapper";
import PasswordSetup from "@/pages/auth/PasswordSetup";
import AdminAuthWrapper from "@/components/auth/AdminAuthWrapper";
import AcceptAdminInvitation from './pages/AcceptAdminInvitation';
import RequestPasswordReset from './pages/auth/RequestPasswordReset';
import ResetPassword from './pages/auth/ResetPassword';

// Create QueryClient with optimized settings
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error: any) => {
                // Don't retry on 4xx errors or specific database errors
                if (error?.status >= 400 && error?.status < 500) return false;
                if (error?.code === 'PGRST200') return false; // Supabase relationship errors
                return failureCount < 2;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
        },
        mutations: {
            retry: 1,
        },
    },
});

// Error Boundary Component
class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error?: Error }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error) {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Application Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md mx-auto text-center p-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-red-800 mb-2">
                                Application Error
                            </h2>
                            <p className="text-red-600 mb-4">
                                Something went wrong. Please try refreshing the page.
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                            >
                                Refresh Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Protected Route Component - Clean Production Version
const ProtectedRoute: React.FC = () => {
    const {user, isLoading, isAuthenticated} = useAuth();

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace/>;
    }

    // Redirect hosts to their own dashboard (no nested routes)
    if (user.role === 'host') {
        return <HostAuthWrapper/>;
    }

    // Allow super_admin to access nested admin routes
    if (user.role === 'super_admin' || user.role === 'org_admin') {
        return (
            <AdminAuthWrapper>
                <Outlet/>
            </AdminAuthWrapper>
        );
    }

    // Access denied for other roles
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md mx-auto text-center p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                        Access Denied
                    </h2>
                    <p className="text-yellow-600 mb-4">
                        You don't have permission to access the admin dashboard.
                    </p>
                    <p className="text-sm text-yellow-600 mb-4">
                        Your role: {user.role}
                    </p>
                    <button
                        onClick={() => window.location.href = '/login'}
                        className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <AccountTypeProvider>
                    <Router
                        future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true,
                        }}
                    >
                        <AccountTypeUrlSync/>
                        <AuthProvider>
                            <Routes>
                                {/* Public routes */}
                                <Route path="/login" element={<Login/>}/>
                                <Route path="/request-password-reset" element={<RequestPasswordReset/>}/>
                                <Route path="/reset-password" element={<ResetPassword/>}/>
                                <Route path="/accept-invitation" element={<AcceptInvitation/>}/>
                                <Route path="/accept-admin-invitation" element={<AcceptAdminInvitation/>}/>

                                {/* Password setup route for hosts */}
                                <Route path="/password-setup" element={<PasswordSetup/>}/>

                                {/* Protected routes */}
                                <Route path="/" element={<ProtectedRoute/>}>
                                    {/* Admin routes - wrapped in AppLayout */}
                                    <Route path="/" element={<AppLayout/>}>
                                        <Route index element={<Navigate to="/dashboard" replace/>}/>
                                        <Route path="dashboard" element={<Dashboard/>}/>
                                        <Route path="organizations" element={<ManageOrganizations/>}/>
                                        <Route path="accounts" element={<ManageAccounts/>}/>
                                        <Route path="accounts/:id" element={<AccountDetail/>}/>
                                        <Route path="game-hosts" element={<ManageGameHost/>}/>
                                        <Route path="host-permissions/:userId" element={<ManageHostPermissions/>}/>
                                        <Route path="games" element={<GameInstances/>}/>
                                        <Route path="admins" element={<ManageAdmins/>}/>
                                    </Route>
                                </Route>

                                {/* Fallback */}
                                <Route path="*" element={<Navigate to="/" replace/>}/>
                            </Routes>
                        </AuthProvider>
                    </Router>
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                        }}
                    />
                </AccountTypeProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
};

export default App;
